<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/campaigns/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.campaigns')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <qr-modal v-model:open="qrModalOpen" :campaign-group="currentCampaignGroup" />

  <div class="ml-12">
    <div class="statistics-row">
      <PayoutStatisticsTable
          :statistics="{
            total: filteredStatistics?.total,
            filtered: filteredStatistics?.filtered,
            max: filteredStatistics?.max,
          }"
          :loading="loadingStatistics"
          :hide-selected="true"
      />
      <div v-if="isFinanceAdmin" class="verification-summary">
        <div class="text-body-regular-bold text-table-text-gray uppercase mb-5">
          {{ $t('pages.campaignList.verificationStats.title') }}
        </div>
        <div v-if="!verificationStatsLoaded" class="verification-placeholder">
          <custom-button variant="primary" :busy="loadingVerificationStatistics" @click="loadVerificationStatistics">
            {{ $t('pages.campaignList.verificationStats.generateButton') }}
          </custom-button>
        </div>
        <busy-area v-else :busy="loadingVerificationStatistics">
          <table class="statisticsTable h-full">
            <tbody>
              <tr>
                <td>{{ $t('pages.campaignList.verificationStats.totalDrivers') }}</td>
                <td>{{ verificationSummary.totalDrivers }}</td>
              </tr>
              <tr>
                <td>{{ $t('pages.campaignList.verificationStats.driversMatched') }}</td>
                <td>{{ verificationSummary.driversMatched }}</td>
              </tr>
              <tr>
                <td>{{ $t('pages.campaignList.verificationStats.matchedPercentage') }}</td>
                <td>{{ verificationSummary.matchedPercentage }}%</td>
              </tr>
            </tbody>
          </table>
        </busy-area>
      </div>
    </div>

    <div v-if="isFinanceAdmin" class="text-body-regular-bold text-table-text-gray uppercase mb-5">
      {{ $t('navigation.campaigns.DEFAULT') }}
    </div>
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :actions="actions"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {
  ref, reactive, computed, unref, watch,
} from 'vue';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, { type Column } from '@components/utils/table/Table.vue';
import {
  collOptionsProvider,
  type ProviderParams,
  useProvider
} from '@components/utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import type { HydraCollection, CampaignGroup, Company, Campaign } from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Region } from '@/entities/Entities';
import Export from '@utils/toolbar/panes/Export.vue';
import { useI18n } from 'vue-i18n';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import { useQueryRef } from '@/hooks/url';
import QrModal from '@pages/campaigns/QrModal.vue';
import BusyArea from '@utils/busy/BusyArea.vue';
import type {Statistics} from '@utils/PayoutStatisticsTable.vue';
import PayoutStatisticsTable from '@utils/PayoutStatisticsTable.vue';
import type { CampaignOpsVerificationUploadStatisticsItem } from '@utils/CampaignOpsVerificationStatisticsTable.vue';
import {useStore} from '@/store';
import {useTableFields} from '@hooks/table-configuration';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'actualStartDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const currentCampaignGroup = ref();
const qrModalOpen = ref(false);

const { t } = useI18n();

const auth = useStore()
const isFinanceAdmin = computed(() => auth.getters['auth/IS_FINANCE_ADMIN']);

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.campaignGroupList.actions.qrCode'),
    action: (item: Campaign) => {
      currentCampaignGroup.value = item.group;
      qrModalOpen.value = true;
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaignList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'group.name',
    title: t('campaign.groupName'),
    sortable: true,
    visible: true,
    group: t('pages.campaignList.fieldGroups.general'),
    to: (campaign: Campaign) => ({
      name: 'campaign-group-details',
      params: { campaignGroupId: campaign?.group?.id },
      query: {
        globalFilter:
          campaign?.region?.id
            ? encodeURIComponent(JSON.stringify({ regions: [campaign.region.id] }))
            : undefined
      }
    }),
    filter: {
      key: 'group',
      type: 'dropdown',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 500,
        createOption: false,
        resolveOnLoad: true,
        noOptionsText: t('inputSearchString'),
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
        label: t('campaign.groupName'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('campaign.region'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('campaign.region'),
      },
    },
  },
  {
    key: 'timeStatus',
    csvTranslations: {
      ongoing: t('campaign.timeStatus.ongoing'),
      future: t('campaign.timeStatus.future'),
      past: t('campaign.timeStatus.past'),
    },
    title: t('campaign.timeStatus.DEFAULT'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (c: Campaign) => {
      const now = new Date();
      const startDate = new Date(c.actualStartDate);
      const endDate = new Date(c.actualEndDate);
      if(startDate <= now && endDate >= now) {
        return t('campaign.timeStatus.ongoing');
      } else if(startDate > now) {
        return t('campaign.timeStatus.future');
      } else {
        return t('campaign.timeStatus.past');
      }
    },
    filter: {
      type: 'dropdown',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        options: [
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
        ],
        label: t('campaign.timeStatus.DEFAULT'),
      },
    },
  },
  {
    key: 'actualStartDate',
    title: t('campaign.startDate'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.startDate'),
      },
    },
  },
  {
    key: 'actualEndDate',
    title: t('campaign.endDate'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      type: 'datetime',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.endDate'),
      },
    },
  },
  {
    key: 'driversCount',
    title: t('campaign.bookingNumbers'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    computed: (c: Campaign) => `${c.bookingNumbers?.booked} / ${c.bookingNumbers?.slots}`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.driversCount'),
      },
    },
  },
  {
    key: 'numOfDriversWithBox',
    title: t('campaign.numOfDriversWithBox'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfDriversWithBox'),
      },
    },
  },
  {
    key: 'numOfDriversWithNoticeComment',
    title: t('campaign.numOfDriversWithNoticeComment'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfDriversWithNoticeComment'),
      },
    },
  },
  {
    key: 'numOfFreeSlotsInRegion',
    title: t('campaign.numOfFreeSlotsInRegion'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: false,
    visible: true,
  },
  {
    key: 'trackedDistance',
    title: t('campaign.trackedDistance'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => `${(v / 1000).toFixed(2)} Km`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>km</span>,
        label: t('campaign.trackedDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'trackedTime',
    title: t('campaign.trackedTime'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('campaign.trackedTime'),
      },
    },
  },
  {
    key: 'numOfTours',
    title: t('campaign.numOfTours'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTours'),
      },
    },
  },
  {
    key: 'numOfTopPhotos',
    title: t('campaign.numOfTopPhotos'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        label: t('campaign.numOfTopPhotos'),
      },
    },
  },
  {
    key: 'avgSpeed',
    title: t('campaign.avgSpeed'),
    group: t('pages.campaignList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    unit: 'km/h',
    formatter: (v: number) => `${(v * 3.6).toFixed(2)} Km/h`,
    filter: {
      type: 'range',
      group: t('pages.campaignList.filterGroups.statistics'),
      options: {
        icon: <span>km/h</span>,
        label: t('campaign.avgSpeed'),
        transformOut: (v: number) => v / 3.6,
        transformIn: (v: number) => v * 3.6,
      },
    },
  },
  {
    key: 'active',
    title: t('campaign.active'),
    group: t('pages.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.active'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('campaignGroup.createdAt'),
    group: t('pages.campaignGroupList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.campaignGroupList.filterGroups.general'),
      options: {
        label: t('campaignGroup.createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: filteredStatistics,
  loading: loadingStatistics,
  refetch: refetchStatistics,
} = useProvider<Statistics>('/campaigns/overall-payout-statistics', providerParams);

const verificationStatsLoaded = ref(false);
const {
  data: verificationStatistics,
  loading: loadingVerificationStatistics,
  refetch: refetchVerificationStatistics,
} = useProvider<CampaignOpsVerificationUploadStatisticsItem[]>(
  '/campaigns/verification-upload-statistics',
  providerParams,
  {
    disabled: true,
    immediate: false,
  },
);

const loadVerificationStatistics = async () => {
  await refetchVerificationStatistics();
  verificationStatsLoaded.value = true;
};

const verificationSummary = computed(() => {
  const stats = unref(verificationStatistics) ?? [];
  const driversMatched = stats.reduce((sum, item) => sum + item.matchedDriverCount, 0);
  const totalDrivers = stats.reduce((sum, item) => sum + item.eligibleDriverCount, 0);
  const matchedPercentage = totalDrivers === 0
    ? '0.0'
    : ((driversMatched / totalDrivers) * 100).toFixed(1);

  return {
    totalDrivers,
    driversMatched,
    matchedPercentage,
  };
});

const {
  data: items,
  loading,
  refetch: refetchItems,
} = useProvider<HydraCollection<Campaign>>('/campaigns', providerParams);

const refetch = () => {
  refetchItems();
  refetchStatistics();
}

watch([filter, sortKey, sortDirection, page, perPage], () => {
  verificationStatsLoaded.value = false;
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}

.statistics-row {
  @apply flex flex-col xl:flex-row xl:items-start gap-10;
}

.verification-summary {
  @apply flex flex-col justify-stretch pt-5 pb-10 w-[360px] max-w-full;
}

.verification-placeholder {
  @apply min-h-[168px] flex items-center justify-center px-5;
}

.statisticsTable {
  @apply border;

  tr {
    td, th {
      text-align: right;
      @apply px-5 py-2;

      &:not(:last-child) {
        @apply border-r;
      }
    }

    th {
      @apply border-b;
    }

    &:not(:last-child) {
      td {
        @apply border-b;
      }
    }

    &:last-child {
      td:not(:first-child) {
        border-top: 3px double black;
      }
    }

    td:first-child {
      text-align: left;
    }

    td:last-child {
      text-align: right;
    }
  }
}
</style>
